import { Invoice, InvoiceLineItem, InvoiceLineItemDetails, SKU, ValidationMismatchEntry } from 'src/redux/billing/billing.types';
import { BillingFilterType } from '../types/billing.types';

/** One validation mismatch to show when attention_needed is true */
export interface AttentionReason {
  label: string;
  expected: string;
  actual: string;
  /** API field name for updating the line item (e.g. impressions, advertiser_spend, margin_pct) */
  fieldKey: string;
  /** Raw value to send when applying the expected value */
  expectedValue: number;
}

/**
 * Format currency amount
 */
export const formatCurrency = (amount: number, currency: string = 'USD', minimumFractionDigits: number = 2, maximumFractionDigits: number = 2): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
  }).format(amount);
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date and time for display
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date and time for display
 */
export const formatDateTimeVerbose = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) {
    return 'Just now';
  }
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  }
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  }
  if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  }
  // If it's more than a week ago, display just the date (no time)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Validate billing filter type
 */
export const isValidBillingFilter = (filter: string): filter is BillingFilterType => {
  return filter === 'insertion-orders' || filter === 'invoices';
};

/**
 * Get display name for billing filter
 */
export const getBillingFilterDisplayName = (filter: BillingFilterType): string => {
  switch (filter) {
    case 'insertion-orders':
      return 'Insertion Orders';
    case 'invoices':
      return 'Invoices';
    default:
      return 'Unknown';
  }
};

/**
 * Generate unique key for billing items
 */
export const generateBillingItemKey = (prefix: string, id: string): string => {
  return `${prefix}-${id}`;
};

/**
 * Debounce function for search
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const fieldValue = (lineItem: InvoiceLineItem, field: string) => {
  return lineItem[field as keyof InvoiceLineItem]?.toString();
};

/** Bill type on the line item's SKU when `skus` is available (from `sku_override` or `sku`). */
export const getBillTypeForLineItemSku = (
  lineItem: InvoiceLineItem,
  skus: SKU[] | null | undefined
): string | null => {
  const id = lineItem.sku_override ?? lineItem.sku;
  if (id == null || id === '' || !skus?.length) return null;
  return skus.find((s) => s.id === String(id))?.bill_type ?? null;
};

/**
 * Derive unit rate and margin dollars from firm inputs (qty, margin %, spend),
 * aligning with `calculateInterconnectedValues` in `utils.tsx`: margin = spend × margin_pct.
 * For `margin_only` SKUs, amount to bill is margin dollars only (not spend + margin).
 */
export const computeDerivedLineItemRateAndMargin = (
  lineItem: InvoiceLineItem,
  billType?: string | null
) => {
  const spend = Number(lineItem.advertiser_spend_override ?? lineItem.advertiser_spend ?? 0);
  const marginPct = Number(lineItem.margin_pct_override ?? lineItem.margin_pct ?? 0);
  const qty = Number(lineItem.qty ?? 0);
  const marginDollars = spend * marginPct;
  const normalized = billType?.trim().toLowerCase() ?? '';
  const amountToBill = normalized === 'margin_only' ? marginDollars : spend + marginDollars;
  const rate = qty > 0 ? amountToBill / qty : 0;
  return { rate, marginDollars, amountToBill };
};

/**
 * Total due for the invoice drawer: sum of each visible line's derived amount to bill.
 * Uses the same `!hide` filter as the drawer line table.
 * Pass `skus` so margin-only SKU lines contribute margin dollars only (matching line labels).
 */
export const computeInvoiceDrawerTotalDue = (
  invoice: Invoice | null | undefined,
  skus?: SKU[] | null
): number => {
  if (!invoice?.invoice_line_items?.length) return 0;
  return invoice.invoice_line_items
    .filter((li) => !li.hide)
    .reduce(
      (sum, li) =>
        sum + computeDerivedLineItemRateAndMargin(li, getBillTypeForLineItemSku(li, skus)).amountToBill,
      0
    );
};

const valuesMatch = (expected: number, actual: number, tolerance = 1e-9): boolean =>
  Math.abs(expected - actual) <= tolerance;

/**
 * Returns reasons why attention is needed (validation mismatches) when details.validations is present.
 * Only includes fields where the expected value does not match the current field value.
 */
export const getAttentionReasons = (
  lineItem: InvoiceLineItem,
  formatCurrencyFn: (n: number) => string,
  formatPercentageFn: (n: number) => string,
  formatNumberFn: (n: number) => string
): AttentionReason[] => {
  const details: InvoiceLineItemDetails | null | undefined = lineItem.details;
  const validations = details?.validations;
  if (!lineItem.attention_needed || !validations) return [];

  const reasons: AttentionReason[] = [];

  if (validations.impressions != null) {
    const expectedVal = Number(validations.impressions);
    const actualVal = Number(details?.impressions ?? lineItem.impressions ?? 0);
    if (!valuesMatch(expectedVal, actualVal)) {
      reasons.push({
        label: 'Impressions',
        expected: formatNumberFn(expectedVal),
        actual: formatNumberFn(actualVal),
        fieldKey: 'impressions',
        expectedValue: expectedVal,
      });
    }
  }
  if (validations.advertiser_spend != null) {
    const expectedVal = Number(validations.advertiser_spend);
    const actualVal = Number(details?.advertiser_spend ?? lineItem.advertiser_spend ?? 0);
    if (!valuesMatch(expectedVal, actualVal)) {
      reasons.push({
        label: 'Advertiser spend',
        expected: formatCurrencyFn(expectedVal),
        actual: formatCurrencyFn(actualVal),
        fieldKey: 'advertiser_spend',
        expectedValue: expectedVal,
      });
    }
  }
  if (validations.margin_pct != null) {
    const expectedVal = Number(validations.margin_pct);
    const actualVal = Number(lineItem.margin_pct ?? 0);
    if (!valuesMatch(expectedVal, actualVal)) {
      reasons.push({
        label: 'Margin %',
        expected: formatPercentageFn(expectedVal),
        actual: formatPercentageFn(actualVal),
        fieldKey: 'margin_pct',
        expectedValue: expectedVal,
      });
    }
  }

  return reasons;
};

/** Map API mismatch field names to display label and PATCH field key */
const MISMATCH_FIELD_CONFIG: Record<string, { label: string; fieldKey: string; format: 'number' | 'currency' | 'percentage' }> = {
  quantity: { label: 'Quantity', fieldKey: 'qty', format: 'number' },
  qty: { label: 'Quantity', fieldKey: 'qty', format: 'number' },
  impressions: { label: 'Impressions', fieldKey: 'qty', format: 'number' },
  advertiser_spend: { label: 'Advertiser spend', fieldKey: 'advertiser_spend', format: 'currency' },
  margin_pct: { label: 'Margin %', fieldKey: 'margin_pct', format: 'percentage' },
};

/** Get current numeric value for a field key from a line item */
const getLineItemFieldValue = (lineItem: InvoiceLineItem, fieldKey: string): number => {
  const val = lineItem[fieldKey as keyof InvoiceLineItem];
  return Number(val ?? 0);
};

/**
 * Build attention reasons from invoice_group.validations.mismatches for a given line item.
 * Uses the API mismatch data (field, actual, expected). Only includes fields where the
 * line item's current value does not match the expected value (so already-fixed items are hidden).
 */
export const getAttentionReasonsFromGroupMismatches = (
  validationMismatches: ValidationMismatchEntry[] | undefined,
  lineItemId: string,
  lineItem: InvoiceLineItem,
  formatCurrencyFn: (n: number) => string,
  formatPercentageFn: (n: number) => string,
  formatNumberFn: (n: number) => string
): AttentionReason[] => {
  if (!validationMismatches?.length) return [];

  const reasons: AttentionReason[] = [];
  const seenFieldKeys = new Set<string>();

  for (const entry of validationMismatches) {
    if (entry.line_item_id !== lineItemId || !entry.mismatches?.length) continue;
    for (const m of entry.mismatches) {
      const config = MISMATCH_FIELD_CONFIG[m.field] ?? { label: m.field, fieldKey: m.field, format: 'number' as const };
      if (seenFieldKeys.has(config.fieldKey)) continue;
      seenFieldKeys.add(config.fieldKey);

      const expectedVal = Number(m.expected);
      const currentVal = getLineItemFieldValue(lineItem, config.fieldKey);
      if (valuesMatch(expectedVal, currentVal)) continue;

      const formatVal = (n: number) =>
        config.format === 'currency' ? formatCurrencyFn(n) : config.format === 'percentage' ? formatPercentageFn(n) : formatNumberFn(n);

      reasons.push({
        label: config.label,
        expected: formatVal(expectedVal),
        actual: formatVal(currentVal),
        fieldKey: config.fieldKey,
        expectedValue: expectedVal,
      });
    }
  }

  return reasons;
};

export const isLockedInvoiceStatus = (status: string | undefined): boolean => {
  const normalised = status?.toLowerCase();
  return normalised === 'synced' || normalised === 'paid';
};

export const isInvoiceLocked = (invoice: Invoice, permissions: string[]) => {
  const isBillingAdmin = permissions.includes('billing_admin');
  return isLockedInvoiceStatus(invoice.invoice_status) && !isBillingAdmin;
};

export const canUnlockInvoice = (invoice: Invoice, permissions: string[]) => {
  const isBillingAdmin = permissions.includes('billing_admin');
  return isLockedInvoiceStatus(invoice.invoice_status) && isBillingAdmin;
};

export const canSyncInvoice = (permissions: string[]) => {
  return permissions.includes('billing_admin');
};

export type LineItemFieldValidationOutcome = 'valid' | 'invalid' | 'unknown';

/**
 * Maps API line item field validation status to valid / invalid / unknown.
 * Unknown yields no status icon in the UI.
 */
export function getLineItemFieldValidationOutcome(lineItem: InvoiceLineItem): LineItemFieldValidationOutcome {
  const raw = (lineItem.field_validation_status ?? lineItem.validation_status ?? '').trim().toLowerCase();
  if (!raw) {
    return 'unknown';
  }
  if (['valid', 'passed', 'ok', 'success'].includes(raw)) {
    return 'valid';
  }
  if (['invalid', 'failed', 'error', 'fail'].includes(raw)) {
    return 'invalid';
  }
  if (['unknown', 'pending', 'none'].includes(raw)) {
    return 'unknown';
  }
  return 'unknown';
}

/**
 * Maps `invoice_line_item_validations.field` from the API to a PATCH key on {@link InvoiceLineItem}.
 */
export function mapInvoiceLineItemValidationFieldToLineItemKey(field: string): keyof InvoiceLineItem | null {
  const f = field.trim().toLowerCase();
  if (f === 'impressions') {
    return 'impressions';
  }
  if (f === 'advertiser_spend' || f === 'spend') {
    return 'advertiser_spend';
  }
  if (f === 'margin_pct') {
    return 'margin_pct';
  }
  if (f === 'qty' || f === 'quantity') {
    return 'qty';
  }
  if (f === 'net_spend') {
    return 'net_spend';
  }
  const keys: (keyof InvoiceLineItem)[] = [
    'impressions',
    'advertiser_spend',
    'margin_pct',
    'qty',
    'advertiser_spend_override',
    'margin_pct_override',
  ];
  if (keys.includes(f as keyof InvoiceLineItem)) {
    return f as keyof InvoiceLineItem;
  }
  return null;
}

/**
 * Parses validation `value` for PATCHing the line item field.
 */
export function parseValidationValueForLineItemField(
  fieldKey: keyof InvoiceLineItem,
  raw: string | null
): number | string | null {
  if (raw == null || raw === '') {
    return null;
  }
  const numericKeys: (keyof InvoiceLineItem)[] = [
    'impressions',
    'advertiser_spend',
    'net_spend',
    'margin_pct',
    'qty',
    'advertiser_spend_override',
    'margin_pct_override',
  ];
  if (numericKeys.includes(fieldKey)) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return raw;
}